import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import LoginComponent from '../../../custom/components/auth/LoginComponent';
import ForgotPasswordComponent from '../../../custom/components/auth/ForgotPasswordComponent';
import VerifyOtpComponent from '../../../custom/components/auth/VerifyOtpComponent';
import ResetPasswordComponent from '../../../custom/components/auth/ResetPasswordComponent';
import { useAppDispatch, useAppSelector } from '../../../custom/redux/reduxHooks';
import { logoutUser, resetUserError } from '../../../custom/redux/reducers/authReducer';
import { toast } from 'react-toastify';
import Popovers from '../../../components/bootstrap/Popovers';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import Icon from '../../../components/icon/Icon';
import ThemeContext from '../../../contexts/themeContext';
import Spinner from '../../../components/bootstrap/Spinner';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import LANG, { ILang, getLangWithKey } from '../../../lang';
import showNotification from '../../../components/extras/showNotification';
import { useTranslation } from 'react-i18next';
import { isAdminRole } from '../../../helpers/adminRoles';
import '../../../custom/components/auth/auth.css';

type AuthStep = 'login' | 'forgot-password' | 'verify-otp' | 'reset-password';

const Login = () => {
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const { i18n } = useTranslation();
	const [currentStep, setCurrentStep] = useState<AuthStep>('login');
	const [resetEmail, setResetEmail] = useState('');

	const changeLanguage = (lng: ILang['key']['lng']) => {
		i18n.changeLanguage(lng).then();
		showNotification(
			<span className='d-flex align-items-center'>
				<Icon icon={getLangWithKey(lng)?.icon} size='lg' className='me-1' />
				<span>{`Language changed to ${getLangWithKey(lng)?.text}`}</span>
			</span>,
			'You updated the language of the site. (Only "Aside" was prepared as an example.)',
		);
	};

	const styledBtn: IButtonProps = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};
	const navigate = useNavigate();
	const { user } = useAppSelector((state) => state.auth);
	const dispatch = useAppDispatch();

	useEffect(() => {
		const checkLoginStatus = async () => {
			if (!user?.role?.roleName) return;

			const hasAdminAccess = await isAdminRole(user.role?.roleName);
			
			if (user && user.token && hasAdminAccess) {
				navigate('/');
			} else if (user && user.role && !hasAdminAccess) {
				toast(
					<>
						<p>You are not authorized to access the admin panel</p>
						<p>Contact System admin to get authorized</p>
					</>,
					{
						type: 'error',
						position: 'top-right',
						theme: darkModeStatus ? 'dark' : 'light',
						onClose: () => dispatch(resetUserError()),
					},
				);
				console.log("USERRRR", user)
				dispatch(logoutUser());
			}
		};

		checkLoginStatus();
	}, [user, navigate, darkModeStatus, dispatch]);

	const handleForgotPassword = () => {
		setCurrentStep('forgot-password');
	};

	const handleBackToLogin = () => {
		setCurrentStep('login');
		setResetEmail('');
	};

	const handleTokenSent = (email: string) => {
		setResetEmail(email);
		setCurrentStep('verify-otp');
	};

	const handleOtpVerified = () => {
		setCurrentStep('reset-password');
	};

	const handleResetSuccess = () => {
		setCurrentStep('login');
		setResetEmail('');
	};

	const renderAuthContent = () => {
		switch (currentStep) {
			case 'forgot-password':
				return (
					<ForgotPasswordComponent
						onBack={handleBackToLogin}
						onTokenSent={handleTokenSent}
					/>
				);
			case 'verify-otp':
				return (
					<VerifyOtpComponent
						email={resetEmail}
						onBack={() => setCurrentStep('forgot-password')}
						onVerified={handleOtpVerified}
					/>
				);
			case 'reset-password':
				return (
					<ResetPasswordComponent
						email={resetEmail}
						onBack={() => setCurrentStep('verify-otp')}
						onSuccess={handleResetSuccess}
					/>
				);
			default:
				return <LoginComponent onForgotPassword={handleForgotPassword} />;
		}
	};

	return (
		<PageWrapper isProtected={false} title={'Admin Login'}>
			<Page className='p-0 position-relative overflow-hidden'>
				{/* Background Elements */}
				<div className='position-absolute top-0 start-0 w-100 h-100'>
					<div 
						className={classNames('position-absolute', {
							'bg-gradient-primary': !darkModeStatus,
							'bg-gradient-dark': darkModeStatus,
						})}
						style={{
							width: '150%',
							height: '150%',
							top: '-25%',
							left: '-25%',
							transform: 'rotate(-15deg)',
							opacity: 0.1,
						}}
					/>
					<div className='position-absolute top-0 end-0'>
						<Icon
							icon='AdminPanelSettings'
							size='5x'
							color='primary'
							style={{ opacity: 0.1, marginTop: '2rem', marginRight: '2rem' }}
						/>
					</div>
					<div className='position-absolute bottom-0 start-0'>
						<Icon
							icon='Security'
							size='4x'
							color='info'
							style={{ opacity: 0.1, marginBottom: '3rem', marginLeft: '3rem' }}
						/>
					</div>
				</div>

				{/* Top Controls */}
				{/* <div className='position-absolute top-0 end-0 p-3 d-flex gap-2'>
					<Dropdown>
						<DropdownToggle hasIcon={false}>
							{typeof getLangWithKey(i18n.language as ILang['key']['lng'])
								?.icon === 'undefined' ? (
								<Button
									{...styledBtn}
									className='btn-only-icon'
									aria-label='Change language'
									data-tour='lang-selector'>
									<Spinner isSmall inButton='onlyIcon' isGrow />
								</Button>
							) : (
								<Button
									{...styledBtn}
									icon={
										getLangWithKey(i18n.language as ILang['key']['lng'])
											?.icon
									}
									aria-label='Change language'
									data-tour='lang-selector'
								/>
							)}
						</DropdownToggle>
						<DropdownMenu isAlignmentEnd data-tour='lang-selector-menu'>
							{Object.keys(LANG).map((i) => (
								<DropdownItem key={LANG[i].lng}>
									<Button
										icon={LANG[i].icon}
										onClick={() => changeLanguage(LANG[i].lng)}>
										{LANG[i].text}
									</Button>
								</DropdownItem>
							))}
						</DropdownMenu>
					</Dropdown>

					<Popovers trigger='hover' desc='Dark / Light mode'>
						<Button
							{...styledBtn}
							onClick={() => setDarkModeStatus(!darkModeStatus)}
							className='btn-only-icon'
							data-tour='dark-mode'>
							<Icon
								icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
								color={darkModeStatus ? 'info' : 'warning'}
								className='btn-icon'
							/>
						</Button>
					</Popovers>

					<Popovers trigger='hover' desc='Fullscreen'>
						<Button
							{...styledBtn}
							icon={fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'}
							onClick={() => setFullScreenStatus(!fullScreenStatus)}
							aria-label='Toggle fullscreen'
						/>
					</Popovers>
				</div> */}

				{/* Main Content */}
				<div className='d-flex align-items-center justify-content-center min-vh-100 position-relative'>
					<div className='container'>
						<div className='row justify-content-center'>
							<div className='col-xl-5 col-lg-6 col-md-8 col-sm-10'>
								<Card 
									className='shadow-lg border-0' 
									data-tour='login-page'
									style={{ backdropFilter: 'blur(10px)' }}
								>
									<CardBody className='p-5'>
										{/* Logo and Title */}
										<div className='text-center mb-5'>
											<div className='d-flex justify-content-center mb-4'>
												<div 
													className='logo-container'
													style={{
														padding: '12px',
														borderRadius: '20px',
														background: darkModeStatus 
															? 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
															: 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.02) 100%)',
														backdropFilter: 'blur(10px)',
														border: `1px solid ${darkModeStatus ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
														boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
													}}
												>
													<Logo width={80} height={80} />
												</div>
											</div>
											<h2 className={classNames('mb-2', {
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											})}>
												{currentStep === 'login' && 'Admin Portal'}
												{currentStep === 'forgot-password' && 'Password Recovery'}
												{currentStep === 'verify-otp' && 'Verify Reset Code'}
												{currentStep === 'reset-password' && 'Set New Password'}
											</h2>
											<p className='text-muted mb-0'>
												{currentStep === 'login' && 'Sign in to access your admin dashboard'}
												{currentStep === 'forgot-password' && 'Enter your email to receive a reset code'}
												{currentStep === 'verify-otp' && 'Enter the code sent to your email'}
												{currentStep === 'reset-password' && 'Create a new secure password'}
											</p>
										</div>

										{/* Auth Content */}
										{renderAuthContent()}

										{/* Footer */}
										<div className='text-center mt-4'>
											<small className='text-muted'>
												© 2025 ZApp Admin Portal. All rights reserved.
											</small>
										</div>
									</CardBody>
								</Card>
							</div>
						</div>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default Login;
